import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Title from '../layouts/Title';
import ContactLeft from './ContactLeft';
import { RiMessage2Fill } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { sendContactMessage } from '../../api';

const Contact = () => {
  const { t, i18n } = useTranslation();

  const [formKey, setFormKey] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const emailValidation = () => {
    return String(formData.email)
      .toLowerCase()
      .match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/);
  };

  const mutation = useMutation({
    mutationFn: (data) => sendContactMessage(data, i18n.language),

    onSuccess: () => {
      setSuccessMsg(
        `${t("contact.form.successMessage.leftMessage")} ${formData.name}، ${t("contact.form.successMessage.rightMessage")}`
      );

      setErrMsg("");

      // تفريغ كامل للـ form
      setFormData({
        name: "",
        phone: "",
        email: "",
        subject: "",
        message: "",
      });

      // إعادة بناء الـ form بالكامل
      setFormKey(prev => prev + 1);

      // reset حالة mutation
      mutation.reset();
    },

    onError: (error) => {
      setErrMsg(
        error?.response?.data?.message ||
        error.message ||
        t("contact.form.errMessage.Something")
      );

      setSuccessMsg("");
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errMsg) setErrMsg("");
  };

  const handleSend = (e) => {
    e.preventDefault();

    setErrMsg("");
    setSuccessMsg("");

    if (!formData.name.trim()) {
      setErrMsg(t("contact.form.errMessage.userName"));
      return;
    }

    if (!formData.phone.trim()) {
      setErrMsg(t("contact.form.errMessage.phoneNumber"));
      return;
    }

    if (!formData.email.trim()) {
      setErrMsg(t("contact.form.errMessage.email"));
      return;
    }

    if (!emailValidation()) {
      setErrMsg(t("contact.form.errMessage.emailVal"));
      return;
    }

    if (!formData.subject.trim()) {
      setErrMsg(t("contact.form.errMessage.subject"));
      return;
    }

    if (!formData.message.trim()) {
      setErrMsg(t("contact.form.errMessage.message"));
      return;
    }

    mutation.mutate(formData);
  };

  return (
    <section
      className={`w-full py-20 border-b-[1px] border-b-black ${
        i18n.language === 'ar' ? "ar-dir" : 'en-dir'
      }`}
    >
      <div className="flex justify-center items-center text-center">
        <Title
          title={`${t("contact.feature")}`}
          des={`${t("contact.title")}`}
        />
      </div>

      <div className="w-full">
        <div className="w-full h-auto flex flex-col lgl:flex-row justify-between">
          <ContactLeft />

          <div className="w-full lgl:w-[60%] h-full py-10 bg-gradient-to-r from-[#1e2024] to-[#23272b] flex flex-col gap-8 p-4 lgl:p-8 rounded-lg shadow-shadowOne">

            <h2 className="text-center text-3xl md:text-4xl font-bold text-[#ff014f] mb-6">
              <span className="inline-flex items-center justify-center gap-2">
                {t("contact.form.title")}
                <RiMessage2Fill className="text-3xl mt-3 ml-1" />
              </span>
            </h2>

            <form
              key={formKey}
              onSubmit={handleSend}
              autoComplete="off"
              className="w-full flex flex-col gap-4 lgl:gap-6 py-2 lgl:py-5"
            >

              <div className="w-full flex flex-col lgl:flex-row gap-10">

                <div className="w-full lgl:w-1/2 flex flex-col gap-4">
                  <p className="text-sm text-gray-400 uppercase tracking-wide">
                    {t("contact.form.name")}
                  </p>

                  <input
                    onChange={handleChange}
                    value={formData.name}
                    name="name"
                    className="contactInput"
                    type="text"
                  />
                </div>

                <div className="w-full lgl:w-1/2 flex flex-col gap-4">
                  <p className="text-sm text-gray-400 uppercase tracking-wide">
                    {t("contact.form.phoneNumber")}
                  </p>

                  <input
                    onChange={handleChange}
                    value={formData.phone}
                    name="phone"
                    className="contactInput"
                    type="text"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-400 uppercase tracking-wide">
                  {t("contact.form.email")}
                </p>

                <input
                  onChange={handleChange}
                  value={formData.email}
                  name="email"
                  className="contactInput"
                  type="email"
                />
              </div>

              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-400 uppercase tracking-wide">
                  {t("contact.form.subject")}
                </p>

                <input
                  onChange={handleChange}
                  value={formData.subject}
                  name="subject"
                  className="contactInput"
                  type="text"
                />
              </div>

              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-400 uppercase tracking-wide">
                  {t("contact.form.message")}
                </p>

                <textarea
                  onChange={handleChange}
                  value={formData.message}
                  name="message"
                  className="contactTextArea"
                  cols="30"
                  rows="8"
                />
              </div>

              <div className="w-full">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="w-full h-12 bg-[#141518] rounded-lg text-base text-gray-400 tracking-wider uppercase hover:text-white duration-300 hover:border-[1px] hover:border-designColor border-transparent disabled:opacity-50"
                >
                  {mutation.isPending
                    ? (t("contact.form.sending") || "Sending...")
                    : t("contact.form.buttonText")}
                </button>
              </div>

              {errMsg && (
                <p className="py-3 bg-gradient-to-r from-[#1e2024] to-[#23272b] shadow-shadowOne text-center text-[#ff014f] text-base tracking-wide animate-bounce">
                  {errMsg}
                </p>
              )}

              {successMsg && (
                <p className="py-3 bg-gradient-to-r from-[#1e2024] to-[#23272b] shadow-shadowOne text-center text-green-500 text-base tracking-wide animate-bounce">
                  {successMsg}
                </p>
              )}

            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;